import type { TilePickerShortcutRequest } from "@/app/editorShortcuts";
import { apiFetch } from "@/lib/apiClient";
import { useEditorStore } from "@/state/useEditorStore";
import {
  TILE_CATALOG,
  getTileCatalogPayloadSnapshot,
  getTileSetById,
  isCustomTileSet,
  removeCustomTileAtRuntime,
  resolveTileAssetUrl,
  useTileCatalogStore
} from "@west-marches/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import { getMapChromeTheme } from "./mapChromeTheme";
import TileCatalogBrowser, { type TileCatalogBrowserSection } from "./TileCatalogBrowser";
import {
  buildRootHotkeySlots,
  getTaxonomyDecorationEntries,
  getTaxonomyDetailEntries,
  getTileCatalogBrowserSectionGroups,
} from "./tileCatalogBrowserSections";
import TileGenerator from "./TileGenerator";
import { getTilePickerEscapeAction } from "./tilePickerNavigation";
import {
  commitGeneratorSectionToMemory,
  commitSectionPathToMemory,
  readInitialGeneratorSectionFromMemory,
  readInitialSectionPathFromMemory,
} from "./tilePickerNavigationMemory";
import TileTaxonomyBreadcrumbs from "./TileTaxonomyBreadcrumbs";

const TILE_TAB_ICON_PATH = "assets/tiles/asset_tile_6fab0057-85d0-45c2-a89a-fd02939c74f9.png";
const DECORATIONS_TAB_ICON_PATH = "assets/overlays/Foliage - Tree, conifer 2 (cold).png";

interface TilePickerProps {
  mode?: "tiles" | "decorations";
  onClose: () => void;
  shortcutRequest?: TilePickerShortcutRequest | null;
}

export default function TilePicker({
  mode = "tiles",
  onClose,
  shortcutRequest = null,
}: TilePickerProps) {
  const catalogRevision = useTileCatalogStore((s) => s.revision);
  const selectedTileSetId = useEditorStore((s) => s.selectedTileSetId);
  const selectedDecorationId = useEditorStore((s) => s.selectedDecorationId);
  const selectBrush = useEditorStore((s) => s.selectBrush);
  const selectDecorationPaintSet = useEditorStore((s) => s.selectDecorationPaintSet);
  const deselectBrush = useEditorStore((s) => s.deselectBrush);
  const backdropRef = useRef<HTMLDivElement>(null);
  const lastHandledShortcutRequestIdRef = useRef<number | null>(null);
  const [generatorSection, setGeneratorSectionState] = useState<TileCatalogBrowserSection | null>(
    readInitialGeneratorSectionFromMemory,
  );
  const [sectionPath, setSectionPathState] = useState<TileCatalogBrowserSection[]>(
    readInitialSectionPathFromMemory,
  );

  const setGeneratorSection = useCallback(
    (update: SetStateAction<TileCatalogBrowserSection | null>) => {
      setGeneratorSectionState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        commitGeneratorSectionToMemory(next);
        return next;
      });
    },
    [],
  );

  const setSectionPath = useCallback(
    (update: SetStateAction<TileCatalogBrowserSection[]>) => {
      setSectionPathState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        commitSectionPathToMemory(next);
        return next;
      });
    },
    [],
  );
  const activeSection =
    sectionPath.length > 0 ? sectionPath[sectionPath.length - 1]! : null;
  const [deletingTileSetId, setDeletingTileSetId] = useState<string | null>(null);
  const [hiddenTileSetIds, setHiddenTileSetIds] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeContentTab, setActiveContentTab] = useState<"tiles" | "decorations">(mode);
  const payload = getTileCatalogPayloadSnapshot();
  const hiddenTileSetIdSet = useMemo(() => new Set(hiddenTileSetIds), [hiddenTileSetIds]);
  const theme = getMapChromeTheme();
  const taxonomyKindFilter: "tile" | "overlay" = mode === "decorations" ? "overlay" : "tile";
  const sectionGroups = useMemo(
    () => getTileCatalogBrowserSectionGroups(payload, taxonomyKindFilter),
    [payload, taxonomyKindFilter],
  );
  const rootHotkeySlots = useMemo(
    () => buildRootHotkeySlots(payload, sectionGroups, hiddenTileSetIdSet, 9, taxonomyKindFilter),
    [payload, sectionGroups, hiddenTileSetIdSet, taxonomyKindFilter],
  );
  const detailEntries = useMemo(() => {
    if (!activeSection) {
      return [];
    }

    if (activeSection.kind === "taxonomy") {
      return getTaxonomyDetailEntries(
        payload,
        activeSection.id,
        hiddenTileSetIdSet,
        taxonomyKindFilter,
      );
    }

    return activeSection.tileSetIds
      .filter((tileSetId) => !hiddenTileSetIdSet.has(tileSetId))
      .map((tileSetId, index) => ({
        kind: "tile-set" as const,
        tileSetId,
        sortOrder: index,
      }));
  }, [activeSection, payload, hiddenTileSetIdSet, taxonomyKindFilter]);
  const activeDecorations = useMemo(() => {
    if (!activeSection || activeSection.kind !== "taxonomy") {
      return [];
    }
    return getTaxonomyDecorationEntries(payload, activeSection.id);
  }, [activeSection, payload]);
  // The Decorations tab browses the overlay-kind taxonomy tree. It should be
  // available whenever the catalog has any decoration content, regardless of
  // which tile folder the user has navigated into.
  const canShowDecorations =
    payload.browse.decorations.length > 0 || activeDecorations.length > 0;
  const tileTabIconUrl = useMemo(() => resolveTileAssetUrl(TILE_TAB_ICON_PATH), []);
  const decorationsTabIconUrl = useMemo(
    () => resolveTileAssetUrl(DECORATIONS_TAB_ICON_PATH),
    [],
  );

  useEffect(() => {
    setActiveContentTab(mode);
    // Tile and decoration trees don't share section identifiers; reset the
    // breadcrumb when the mode changes so we always land on the correct tree's
    // root.
    setSectionPath([]);
  }, [mode, setSectionPath]);

  useEffect(() => {
    if (activeContentTab === "decorations" && !canShowDecorations) {
      setActiveContentTab("tiles");
    }
  }, [activeContentTab, canShowDecorations]);

  useEffect(() => {
    if (!shortcutRequest) {
      return;
    }

    if (lastHandledShortcutRequestIdRef.current === shortcutRequest.id) {
      return;
    }
    lastHandledShortcutRequestIdRef.current = shortcutRequest.id;

    if (shortcutRequest.kind === "escape") {
      const action = getTilePickerEscapeAction({
        hasSectionView: activeSection !== null,
        hasGeneratorView: generatorSection !== null,
      });

      if (action === "tile-set-list") {
        setGeneratorSection(null);
      } else if (action === "section-list") {
        setSectionPath((path) => path.slice(0, -1));
      } else {
        onClose();
      }
      return;
    }

    if (generatorSection) {
      return;
    }

    if (activeSection) {
      if (activeContentTab === "decorations") {
        return;
      }

      const entry = detailEntries[shortcutRequest.index];
      if (!entry) {
        return;
      }

      if (entry.kind === "section") {
        setSectionPath([
          ...sectionPath,
          { ...entry.section, tileSetIds: entry.visibleTileSetIds },
        ]);
        return;
      }

      if (entry.kind !== "tile-set") {
        return;
      }

      selectBrush(entry.tileSetId);
      onClose();
      return;
    }

    const slot = rootHotkeySlots[shortcutRequest.index];
    if (!slot) {
      return;
    }

    if (slot.kind === "section") {
      setSectionPath([
        {
          ...slot.section,
          tileSetIds: slot.visibleTileSetIds,
        },
      ]);
      return;
    }

    selectBrush(slot.tileSetId);
    onClose();
  }, [
    activeContentTab,
    activeSection,
    activeDecorations,
    detailEntries,
    generatorSection,
    onClose,
    rootHotkeySlots,
    sectionPath,
    selectBrush,
    setGeneratorSection,
    setSectionPath,
    shortcutRequest,
  ]);

  async function handleDeleteGroup(tileSetId: string, label: string) {
    const confirmed = window.confirm(`Delete "${label}" from the custom tile catalog?`);
    if (!confirmed) return;

    setDeletingTileSetId(tileSetId);
    setDeleteError(null);

    try {
      const res = await apiFetch("/api/save-tile", {
        method: "DELETE",
        body: JSON.stringify({ tileSetId }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Delete failed");
      }

      const tileSet = getTileSetById(tileSetId);
      for (const tileId of tileSet?.customTileIds ?? []) {
        removeCustomTileAtRuntime(tileSetId, tileId);
      }
      setHiddenTileSetIds((current) => [...current, tileSetId]);
      if (selectedTileSetId === tileSetId) {
        deselectBrush();
      }
    } catch (error) {
      setDeleteError(
        error instanceof Error ? error.message : "Unable to delete custom tile",
      );
    } finally {
      setDeletingTileSetId(null);
    }
  }

  return (
    <div
      ref={backdropRef}
      className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto ${theme.modalBackdrop}`}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div
        data-catalog-revision={catalogRevision}
        className={`relative my-8 mx-4 max-h-[calc(100vh-64px)] w-full max-w-[1200px] overflow-y-auto rounded-2xl p-6 scrollbar-none [scrollbar-gutter:stable_both-edges] ${theme.modalPanel}`}
      >
        {generatorSection ? (
          <TileGenerator
            taxonomyNodeId={generatorSection.id}
            taxonomyLabel={generatorSection.label}
            tileSets={TILE_CATALOG.filter((group) => group.taxonomyNodeIds.includes(generatorSection.id))}
            onClose={() => setGeneratorSection(null)}
          />
        ) : (
          <>
            {deleteError && (
              <div className={theme.pickerError}>
                {deleteError}
              </div>
            )}

            <div className="relative mb-8 min-h-13 py-1">
              <div className="flex min-w-0 justify-start">
                {activeSection !== null ? (
                  <button
                    type="button"
                    onClick={() => setSectionPath((path) => path.slice(0, -1))}
                    aria-keyshortcuts="Escape"
                    className={theme.pickerActionButton}
                  >
                    <span className="flex items-center gap-1.5">
                      <span aria-hidden>&larr;</span>
                      <span>Back</span>
                    </span>
                    <span className={`text-xs font-normal tabular-nums ${theme.pickerShortcut}`}>
                      (Esc)
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close tile picker"
                    aria-keyshortcuts="Escape"
                    className={theme.pickerActionButton}
                  >
                    <span className="flex items-center gap-1.5">
                      <span aria-hidden className="text-lg leading-none font-light">
                        &times;
                      </span>
                      <span>Close</span>
                    </span>
                    <span className={`text-xs font-normal tabular-nums ${theme.pickerShortcut}`}>
                      (Esc)
                    </span>
                  </button>
                )}
              </div>
              <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center px-20">
                <div
                  className="pointer-events-auto relative inline-grid w-fit grid-cols-2 items-center rounded-lg border-2 border-neutral-950 bg-[#fff2d9]/78 p-0.5 shadow-sm"
                  role="tablist"
                  aria-label="Tile picker content"
                >
                  <span
                    aria-hidden
                    data-active-tab-indicator
                    className="pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-md border-2 border-neutral-950 bg-neutral-950 shadow-sm transition-transform duration-200 ease-out"
                    style={{
                      transform: `translateX(${activeContentTab === "tiles" ? "0%" : "100%"})`,
                    }}
                  />
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeContentTab === "tiles"}
                    className={`relative z-10 inline-flex items-center justify-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.11em] transition-colors ${
                      activeContentTab === "tiles" ? theme.tabButtonActive : theme.tabButtonIdle
                    }`}
                    onClick={() => setActiveContentTab("tiles")}
                  >
                    <img
                      src={tileTabIconUrl}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 rounded-[3px] object-cover opacity-100"
                      loading="lazy"
                    />
                    <span
                      className={activeContentTab === "tiles" ? theme.tabButtonActive : "text-neutral-950"}
                    >
                      Tiles
                    </span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeContentTab === "decorations"}
                    aria-disabled={!canShowDecorations}
                    disabled={!canShowDecorations}
                    className={`relative z-10 inline-flex items-center justify-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.11em] transition-colors ${
                      activeContentTab === "decorations"
                        ? theme.tabButtonActive
                        : canShowDecorations
                          ? theme.tabButtonIdle
                          : "cursor-not-allowed text-neutral-950/30"
                    }`}
                    onClick={() => setActiveContentTab("decorations")}
                  >
                    <img
                      src={decorationsTabIconUrl}
                      alt=""
                      aria-hidden="true"
                      className={`h-4 w-4 shrink-0 rounded-[3px] object-contain ${
                        canShowDecorations ? "opacity-100" : "opacity-30"
                      }`}
                      loading="lazy"
                    />
                    <span
                      className={
                        activeContentTab === "decorations"
                          ? theme.tabButtonActive
                          : canShowDecorations
                            ? "text-neutral-950"
                            : "text-neutral-950/30"
                      }
                    >
                      Decorations
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <TileTaxonomyBreadcrumbs
              payload={payload}
              sectionPath={sectionPath}
              hiddenTileSetIds={hiddenTileSetIds}
              onNavigatePath={(path) => {
                setGeneratorSection(null);
                setSectionPath(path);
              }}
            />

            <TileCatalogBrowser
              payload={payload}
              sectionPath={sectionPath}
              onSectionPathChange={setSectionPath}
              activeContentTab={activeContentTab}
              onSelectTileSet={(tileSetId) => {
                selectBrush(tileSetId);
                onClose();
              }}
              onSelectDecoration={({ paintSetId, decorationId, taxonomyNodeId }) => {
                selectDecorationPaintSet(paintSetId, taxonomyNodeId, {
                  kind: "decoration",
                  decorationId,
                });
                onClose();
              }}
              selectedTileSetId={selectedTileSetId}
              selectedDecorationId={selectedDecorationId}
              hiddenTileSetIds={hiddenTileSetIds}
              heading={null}
              detailAction={
                // Tile generation is temporarily disabled.
                // activeSection?.kind === "taxonomy" &&
                // isOfficialRootTaxonomyNode(activeSection.id) ? (
                //   <button
                //     onClick={() => setGeneratorSection(activeSection)}
                //     className={theme.pickerCreateCard}
                //   >
                //     <span className={`text-5xl leading-none ${theme.pickerCreateCardAccent}`}>+</span>
                //     <span className={`text-sm font-medium ${theme.pickerCreateCardAccent}`}>
                //       Generate New Tile
                //     </span>
                //   </button>
                // ) : null
                null
              }
              renderTileSetAction={(tileSet) => {
                const runtimeTileSet = getTileSetById(tileSet.id);
                if (!runtimeTileSet || !isCustomTileSet(runtimeTileSet)) {
                  return null;
                }

                return (
                  <button
                    type="button"
                    onClick={() => void handleDeleteGroup(runtimeTileSet.id, runtimeTileSet.label)}
                    disabled={deletingTileSetId === runtimeTileSet.id}
                    className={theme.pickerDeleteButton}
                  >
                    {deletingTileSetId === runtimeTileSet.id ? "Deleting..." : "Delete"}
                  </button>
                );
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
